import React, { useEffect, useState } from "react";
import { Plus, CalendarCheck, LogIn, LogOut, Pencil, Trash2, ChevronLeft, ChevronRight, CalendarDays, List } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const gbp = (n) => `£${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS = [["enquiry", "Enquiry"], ["confirmed", "Confirmed"], ["checked_in", "Checked in"], ["checked_out", "Checked out"], ["cancelled", "Cancelled"]];
export const BOOKING_STATUS_CLS = {
  enquiry: "bg-secondary text-muted-foreground",
  confirmed: "bg-[#3a4a6b]/12 text-[#3a4a6b]",
  checked_in: "bg-[#2B4C3F]/12 text-[#2B4C3F]",
  checked_out: "bg-[#C6A87C]/20 text-[#8a6d3b]",
  cancelled: "bg-[#9E382A]/12 text-[#9E382A]",
};

const empty = { guest_name: "", guest_id: "", phone: "", email: "", party_size: "", check_in: "", check_out: "", amount: "", cleaning_fee: "", source: "", status: "confirmed", notes: "" };

export function BookingForm({ pid, editing, onDone, trigger, others = [] }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [guests, setGuests] = useState([]);
  useEffect(() => {
    if (open) {
      setF(editing ? { ...empty, ...editing, party_size: editing.party_size ?? "", amount: editing.amount ?? "", cleaning_fee: editing.cleaning_fee ?? "" } : empty);
      api.get("/guests").then((r) => setGuests(r.data)).catch(() => {});
    }
  }, [open, editing]);

  const pickGuest = (gid) => {
    const g = guests.find((x) => x.id === gid);
    if (g) setF((prev) => ({ ...prev, guest_id: gid, guest_name: g.name, email: g.email || "", phone: g.phone || "" }));
  };

  const save = async () => {
    const payload = {
      ...f,
      guest_id: f.guest_id || null,
      party_size: f.party_size === "" ? null : Number(f.party_size),
      amount: f.amount === "" ? 0 : Number(f.amount),
      cleaning_fee: f.cleaning_fee === "" ? 0 : Number(f.cleaning_fee),
    };
    try {
      if (editing) await api.patch(`/bookings/${editing.id}`, payload);
      else await api.post(`/properties/${pid}/bookings`, payload);
      toast.success(editing ? "Booking updated" : "Booking added");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-head">{editing ? "Edit booking" : "Add booking"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto thin-scroll pr-1">
          {guests.length > 0 && !editing && (
            <div>
              <Label className="overline">Use saved guest (optional)</Label>
              <Select value={f.guest_id || ""} onValueChange={pickGuest}>
                <SelectTrigger data-testid="booking-guest-picker" className="mt-1.5"><SelectValue placeholder="Choose from directory…" /></SelectTrigger>
                <SelectContent>{guests.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}{g.email ? ` · ${g.email}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="overline">Guest name</Label><Input data-testid="booking-guest" value={f.guest_name} onChange={(e) => setF({ ...f, guest_name: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Phone</Label><Input data-testid="booking-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="overline">Email</Label><Input data-testid="booking-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Check-in</Label><Input data-testid="booking-checkin" type="date" value={(f.check_in || "").slice(0, 10)} onChange={(e) => setF({ ...f, check_in: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="overline">Check-out</Label><Input data-testid="booking-checkout" type="date" value={(f.check_out || "").slice(0, 10)} onChange={(e) => setF({ ...f, check_out: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="overline">Guests</Label><Input data-testid="booking-party" type="number" value={f.party_size} onChange={(e) => setF({ ...f, party_size: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="overline">Total £</Label><Input data-testid="booking-amount" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="overline">Cleaning £</Label><Input data-testid="booking-cleaning" type="number" value={f.cleaning_fee} onChange={(e) => setF({ ...f, cleaning_fee: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Source</Label><Input data-testid="booking-source" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="Direct / Airbnb…" className="mt-1.5" /></div>
            <div>
              <Label className="overline">Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="booking-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="overline">Notes</Label><Textarea data-testid="booking-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" /></div>
          {Number(f.amount) > 0 && f.status !== "cancelled" && <p className="text-xs text-muted-foreground">{gbp(f.amount)} will be recorded as booking income for the landlord statement.</p>}
          {(() => {
            const ci = (f.check_in || "").slice(0, 10), co = (f.check_out || "").slice(0, 10);
            const clash = ci && co && co > ci && others.some((o) => o.id !== editing?.id && o.status !== "cancelled" && ci < (o.check_out || "").slice(0, 10) && co > (o.check_in || "").slice(0, 10));
            const badDates = ci && co && co <= ci;
            if (badDates) return <p data-testid="booking-baddate" className="text-xs text-[#9E382A]">Check-out must be after check-in.</p>;
            if (clash) return <p data-testid="booking-clash" className="text-xs text-[#9E382A] bg-[#9E382A]/8 rounded-md px-3 py-2">⚠ These dates overlap another booking for this property — possible double-booking.</p>;
            return null;
          })()}
        </div>
        <DialogFooter><Button data-testid="booking-save" onClick={save} disabled={!f.guest_name || !f.check_in || !f.check_out}>{editing ? "Save" : "Add booking"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BookingCalendar({ rows }) {
  const [off, setOff] = useState(0);
  const base = new Date(); base.setDate(1); base.setMonth(base.getMonth() + off);
  const year = base.getFullYear(), month = base.getMonth();
  const lead = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysIn = new Date(year, month + 1, 0).getDate();
  const active = rows.filter((b) => b.status !== "cancelled");
  const iso = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const cells = [...Array(lead).fill(null), ...Array.from({ length: daysIn }, (_, i) => i + 1)];
  return (
    <div className="p-4" data-testid="booking-calendar">
      <div className="flex items-center justify-between mb-3">
        <Button size="icon" variant="ghost" className="h-8 w-8" data-testid="cal-prev" onClick={() => setOff(off - 1)}><ChevronLeft className="h-4 w-4" /></Button>
        <p className="font-head font-semibold">{base.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</p>
        <Button size="icon" variant="ghost" className="h-8 w-8" data-testid="cal-next" onClick={() => setOff(off + 1)}><ChevronRight className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground mb-1">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const ds = iso(d);
          const arr = active.filter((b) => (b.check_in || "").slice(0, 10) === ds);
          const dep = active.filter((b) => (b.check_out || "").slice(0, 10) === ds);
          const occ = active.some((b) => (b.check_in || "").slice(0, 10) <= ds && ds < (b.check_out || "").slice(0, 10));
          const cls = arr.length ? "bg-[#2B4C3F]/15" : occ ? "bg-[#C6A87C]/20" : dep.length ? "bg-[#D98324]/12" : "";
          const title = [...arr.map((b) => "In: " + b.guest_name), ...dep.map((b) => "Out: " + b.guest_name)].join(" · ");
          return (
            <div key={i} title={title} data-testid={`cal-day-${d}`} className={`min-h-[3rem] rounded-md border border-border text-xs p-1 ${cls}`}>
              <div className="font-medium">{d}</div>
              {arr.length > 0 && <div className="text-[9px] truncate text-[#2B4C3F]">▸ {arr[0].guest_name}</div>}
              {dep.length > 0 && <div className="text-[9px] truncate text-[#D98324]">◂ out</div>}
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 mt-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#2B4C3F]" />Arrival</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#C6A87C]" />Occupied</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#D98324]" />Departure</span>
      </div>
    </div>
  );
}

export function BookingsTab({ property, role }) {
  const staff = ["admin", "operations"].includes(role);
  const pid = property.id;
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("list");
  const load = () => api.get(`/properties/${pid}/bookings`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [pid]);

  const del = async (b) => {
    if (!window.confirm("Delete this booking? Its recorded income will be removed too.")) return;
    try { await api.delete(`/bookings/${b.id}`); toast.success("Booking deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const checkIns = new Set(rows.filter((r) => r.status !== "cancelled").map((r) => (r.check_in || "").slice(0, 10)));

  return (
    <div className="bg-white border border-border rounded-md" data-testid="bookings-tab">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <span className="overline flex items-center gap-1.5"><CalendarCheck className="h-3.5 w-3.5" />Bookings & stays</span>
        <div className="flex items-center gap-2">
          <div className="flex border border-border rounded-md overflow-hidden">
            <button data-testid="booking-view-list" onClick={() => setView("list")} className={`px-2 py-1 ${view === "list" ? "bg-[#18181A] text-white" : "text-muted-foreground"}`}><List className="h-4 w-4" /></button>
            <button data-testid="booking-view-calendar" onClick={() => setView("calendar")} className={`px-2 py-1 ${view === "calendar" ? "bg-[#18181A] text-white" : "text-muted-foreground"}`}><CalendarDays className="h-4 w-4" /></button>
          </div>
          {staff && <BookingForm pid={pid} others={rows} onDone={load} trigger={<Button size="sm" data-testid="add-booking-button"><Plus className="h-4 w-4 mr-1" />Add booking</Button>} />}
        </div>
      </div>
      {view === "calendar" ? <BookingCalendar rows={rows} /> : rows.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">No bookings yet.</p> : (
        <div className="divide-y divide-border">
          {rows.map((b) => {
            const turnaround = b.status !== "cancelled" && checkIns.has((b.check_out || "").slice(0, 10)) && (b.check_out || "").slice(0, 10) !== (b.check_in || "").slice(0, 10);
            return (
            <div key={b.id} data-testid={`booking-${b.id}`} className="px-5 py-3 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{b.guest_name}{b.party_size ? ` · ${b.party_size} guests` : ""}{b.source ? ` · ${b.source}` : ""}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                  <LogIn className="h-3 w-3" />{fmtDate(b.check_in)}<span className="mx-0.5">→</span><LogOut className="h-3 w-3" />{fmtDate(b.check_out)}{b.phone ? ` · ${b.phone}` : ""}
                </p>
              </div>
              {turnaround && <span data-testid={`turnaround-${b.id}`} className="text-[10px] px-2 py-0.5 rounded-full bg-[#D98324]/15 text-[#D98324]">Same-day turnaround</span>}
              {b.amount > 0 && <p className="font-mono text-sm">{gbp(b.amount)}</p>}
              <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${BOOKING_STATUS_CLS[b.status]}`}>{b.status.replace("_", " ")}</span>
              {staff && <BookingForm pid={pid} editing={b} others={rows} onDone={load} trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`booking-edit-${b.id}`}><Pencil className="h-3.5 w-3.5" /></Button>} />}
              {staff && <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`booking-delete-${b.id}`} onClick={() => del(b)}><Trash2 className="h-3.5 w-3.5 text-[#9E382A]" /></Button>}
            </div>
          ); })}
        </div>
      )}
    </div>
  );
}
